import React, { useCallback, useState } from 'react';
import { getCarteiraTesouraria } from '../../../services/carteiraDiariaService';
import { getFundosGerenciados } from '../../../services/fundosGerenciadosService';
import { CarteiraDiariaTesouraria } from '../../../types/carteiraDiaria';
import { FundosGerenciadosData } from '../../../types/fundosGerenciados';
import { ColumnDef, SortingState } from '@tanstack/react-table';
import IconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/Add';
import Modal from '@mui/material/Modal';
import { useTableState } from '../../../hooks/useTableState';
import { useSnackbar } from '../../../hooks/useSnackbar';
import CadastroAtivoForm from '../../../components/CadastroAtivoForm';
import SnackbarAlert from '../../../components/SnackbarAlert';
import TableCarteiraDiaria from '../components/TableCarteiraDiaria';

const CarteiraDiaria_SaldoTesouraria: React.FC = () => {
  const [saldo, setCarteiraSaldo] = useState<CarteiraDiariaTesouraria[]>([]);
  const [fundosGerenciados, setFundosGerenciados] = useState<FundosGerenciadosData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [pageCount, setPageCount] = useState(0);
  const [isCadastroModalOpen, setIsCadastroModalOpen] = useState<boolean>(false);

  const { pagination, setPagination, sorting, setSorting, globalFilter, setGlobalFilter } = useTableState(10);
  const { snackbar, showSuccess, showError, close: closeSnackbar } = useSnackbar();

  const fetchData = useCallback(async (pageIndex: number, pageSize: number, sortBy: SortingState, globalFilter: string, dataFilter: string) => {
    setLoading(true);
    try {
      const [tesouraria, fundosData] = await Promise.all([
        getCarteiraTesouraria(pageIndex, pageSize, sortBy, globalFilter, dataFilter),
        getFundosGerenciados(),
      ]);
      setCarteiraSaldo(tesouraria.results);
      setPageCount(tesouraria.totalPages);
      setFundosGerenciados(fundosData);
    } catch {
      showError('Erro ao buscar dados');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  const handleCadastroSuccess = () => {
    fetchData(pagination.pageIndex, pagination.pageSize, sorting, globalFilter, '');
    showSuccess('Cadastro realizado com sucesso!');
    setIsCadastroModalOpen(false);
  };

  const getFundoGerenciadoNome = useCallback((fundoId: any): string => {
    const fundo = fundosGerenciados.find(f => f.id === fundoId);
    return fundo ? fundo.fantasia : 'Fundo desconhecido';
  }, [fundosGerenciados]);

  const columns = React.useMemo<ColumnDef<CarteiraDiariaTesouraria>[]>(() => [
    {
      header: 'Data Posição',
      accessorKey: 'data_posicao',
      cell: (info) => {
        const rawDate = info.getValue() as string;
        const [year, month, day] = rawDate.split('-');
        return `${day}/${month}/${year}`;
      },
      enableSorting: true,
    },
    {
      header: 'Fundo Gerenciados',
      accessorFn: (row) => getFundoGerenciadoNome(row.fundo),
      id: 'fundo__fantasia',
      enableSorting: true,
    },
    { header: 'ADM',        accessorKey: 'adm' },
    { header: 'Descrição',  accessorKey: 'descricao' },
    { header: 'Financeiro', accessorKey: 'financeiro' },
  ], [getFundoGerenciadoNome]);

  return (
    <div>
      <div className="page-header">
        <h2>Carteira Diária Tesouraria</h2>
        <IconButton onClick={() => setIsCadastroModalOpen(true)}>
          <AddIcon />
        </IconButton>
      </div>
      <TableCarteiraDiaria
        columns={columns}
        data={saldo}
        fetchData={fetchData}
        pageCount={pageCount}
        pagination={pagination}
        setPagination={setPagination}
        sorting={sorting}
        setSorting={setSorting}
        globalFilter={globalFilter}
        setGlobalFilter={setGlobalFilter}
        isLoading={loading}
        tipoCarteira='saldo-tesouraria'
      />
      <Modal open={isCadastroModalOpen} onClose={() => setIsCadastroModalOpen(false)}>
        <div>
          <CadastroAtivoForm
            isOpen={isCadastroModalOpen}
            onRequestClose={() => setIsCadastroModalOpen(false)}
            onCadastroSuccess={handleCadastroSuccess}
            setSnackbarProps={() => {}}
          />
        </div>
      </Modal>
      <SnackbarAlert
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={closeSnackbar}
      />
    </div>
  );
};

export default CarteiraDiaria_SaldoTesouraria;
